import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// Branding
const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function TableNo27() {
  const pdfRef = useRef();

  // 📊 Gender share dataset
  const data = [
    {
      ageGroup: "14–18",
      femaleShare2018: 1.78,
      femaleShare2024: 1.96,
      diffFemale: 0.18,
      maleShare2018: 0.97,
      maleShare2024: 0.99,
      diffMale: 0.02,
    },
    {
      ageGroup: "19–21",
      femaleShare2018: 4.25,
      femaleShare2024: 4.16,
      diffFemale: -0.09,
      maleShare2018: 3.81,
      maleShare2024: 3.49,
      diffMale: -0.31,
    },
    {
      ageGroup: "22–24",
      femaleShare2018: 5.66,
      femaleShare2024: 6.1,
      diffFemale: 0.44,
      maleShare2018: 5.31,
      maleShare2024: 5.57,
      diffMale: 0.26,
    },
    {
      ageGroup: "25–34",
      femaleShare2018: 20.56,
      femaleShare2024: 21.73,
      diffFemale: 1.17,
      maleShare2018: 22.7,
      maleShare2024: 23.32,
      diffMale: 0.62,
    },
    {
      ageGroup: "35–44",
      femaleShare2018: 23.51,
      femaleShare2024: 21.93,
      diffFemale: -1.57,
      maleShare2018: 24.59,
      maleShare2024: 23.57,
      diffMale: -1.02,
    },
    {
      ageGroup: "45–54",
      femaleShare2018: 22.43,
      femaleShare2024: 21.77,
      diffFemale: -0.67,
      maleShare2018: 20.41,
      maleShare2024: 20.83,
      diffMale: 0.43,
    },
    {
      ageGroup: "55–64",
      femaleShare2018: 17.23,
      femaleShare2024: 16.01,
      diffFemale: -1.22,
      maleShare2018: 17.9,
      maleShare2024: 16.11,
      diffMale: -1.79,
    },
    {
      ageGroup: "65–99",
      femaleShare2018: 4.58,
      femaleShare2024: 6.35,
      diffFemale: 1.77,
      maleShare2018: 4.32,
      maleShare2024: 6.11,
      diffMale: 1.79,
    },
  ];

  // 🧩 Insights
  const avgFemaleDiff = (
    data.reduce((s, d) => s + d.diffFemale, 0) / data.length
  ).toFixed(2);
  const avgMaleDiff = (
    data.reduce((s, d) => s + d.diffMale, 0) / data.length
  ).toFixed(2);
  const topFemale = data.reduce((max, d) =>
    d.diffFemale > max.diffFemale ? d : max
  );
  const topMale = data.reduce((max, d) =>
    d.diffMale > max.diffMale ? d : max
  );

  // 📈 Chart
  const chartData = {
    labels: data.map((d) => d.ageGroup),
    datasets: [
      {
        label: "Female Share Δ (%)",
        data: data.map((d) => d.diffFemale),
        backgroundColor: data.map((d) =>
          d.diffFemale >= 0 ? "rgba(236, 72, 153, 0.8)" : "rgba(236, 72, 153, 0.3)"
        ),
        borderRadius: 6,
      },
      {
        label: "Male Share Δ (%)",
        data: data.map((d) => d.diffMale),
        backgroundColor: data.map((d) =>
          d.diffMale >= 0 ? "rgba(14, 165, 233, 0.8)" : "rgba(14, 165, 233, 0.8)"
        ),
        borderRadius: 6,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: { color: "#111827", font: { size: 13, weight: "bold" } },
      },
      title: {
        display: true,
        text: "📊 Gender Share Difference by Age Group (2018 → 2024)",
        color: "#111827",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(2)}%`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: false,
        ticks: { callback: (v) => v + "%", color: "#4B5563" },
        title: {
          display: true,
          text: "Change in Share (%)",
          color: "#111827",
          font: { size: 14, weight: "bold" },
        },
      },
      x: {
        ticks: { color: "#4B5563" },
        title: {
          display: true,
          text: "Age Groups",
          color: "#111827",
          font: { size: 14, weight: "bold" },
        },
      },
    },
  };

  // 🧾 PDF Export
  const downloadPDF = () => {
    const input = pdfRef.current;

    html2canvas(input, {
      scale: 2, // higher for sharper text
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      // A4 size in mm
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Convert canvas height to mm
      const imgWidth = pageWidth - 10; // leave small margins
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      // Header
      const addHeader = () => {
        pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      // Footer
      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right"
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // More pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Footer for the last page
      pdf.line(10, 260, 200, 260);
      const footerY = 265;
      pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 55, footerY + 10);
      pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
      pdf.text("using", 112, footerY + 10);
      pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);
      pdf.text("20250916.v3.198", 148, footerY + 10);
      pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 150, 295);
      pdf.text(`From: ${window.location.href}`, 10, 295);

      pdf.save("gender-share-analysis.pdf");
      // input.style.width = originalWidth;
    });
  };

  // 💅 UI
  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white from-blue-50 via-white to-rose-50 rounded-xl shadow-lg"
      >
        {/* Header */}
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Gender Share Analysis (2018 → 2024)
        </h2>

        {/* Insight Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-rose-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Highest Female Gain</p>
            <p className="text-xl font-bold text-rose-700">
              {topFemale.ageGroup} (+{topFemale.diffFemale.toFixed(2)}%)
            </p>
          </div>
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Highest Male Gain</p>
            <p className="text-xl font-bold text-blue-700">
              {topMale.ageGroup} (+{topMale.diffMale.toFixed(2)}%)
            </p>
          </div>
          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Average Δ (2018→2024)</p>
            <p className="text-xl font-bold text-green-700">
              ♀ {avgFemaleDiff}% | ♂ {avgMaleDiff}%
            </p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="overflow-x-auto rounded-lg shadow-md">
            <table className="w-full border-collapse text-center">
              <thead className="bg-blue-700 text-white">
                <tr>
                  <th className="p-2">Age Group</th>
                  <th className="p-2">♀ 2018</th>
                  <th className="p-2">♀ 2024</th>
                  <th className="p-2">Δ Female</th>
                  <th className="p-2">♂ 2018</th>
                  <th className="p-2">♂ 2024</th>
                  <th className="p-2">Δ Male</th>
                </tr>
              </thead>
              <tbody>
                {data.map((d, i) => {
                  const rowHighlight =
                    d.diffFemale > 1 || d.diffMale > 1
                      ? "bg-green-100"
                      : d.diffFemale < -1 || d.diffMale < -1
                        ? "bg-yellow-100"
                        : "bg-white";
                  return (
                    <tr key={i} className={`hover:bg-gray-100 ${rowHighlight}`}>
                      <td className="p-2 font-semibold">{d.ageGroup}</td>
                      <td className="p-2 text-rose-700">
                        {d.femaleShare2018.toFixed(2)}%
                      </td>
                      <td className="p-2 text-rose-700">
                        {d.femaleShare2024.toFixed(2)}%
                      </td>
                      <td
                        className={`p-2 ${
                          d.diffFemale > 0
                            ? "text-green-700"
                            : d.diffFemale < 0
                              ? "text-yellow-700"
                              : "text-gray-600"
                        }`}
                      >
                        {d.diffFemale > 0 ? "+" : ""}
                        {d.diffFemale.toFixed(2)}%
                      </td>
                      <td className="p-2 text-blue-700">
                        {d.maleShare2018.toFixed(2)}%
                      </td>
                      <td className="p-2 text-blue-700">
                        {d.maleShare2024.toFixed(2)}%
                      </td>
                      <td
                        className={`p-2 ${
                          d.diffMale > 0
                            ? "text-green-700"
                            : d.diffMale < 0
                              ? "text-yellow-700"
                              : "text-gray-600"
                        }`}
                      >
                        {d.diffMale > 0 ? "+" : ""}
                        {d.diffMale.toFixed(2)}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Chart */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <div style={{ height: "400px", width: "100%" }}>
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="text-center mt-4 text-gray-700">
          <p className="text-sm">
            📌{" "}
            <span className="font-semibold text-rose-700">
              {topFemale.ageGroup}
            </span>{" "}
            saw the highest female growth, while{" "}
            <span className="font-semibold text-blue-700">
              {topMale.ageGroup}
            </span>{" "}
            leads in male share improvement between 2018 and 2024.
          </p>
        </div>
      </div>

      {/* PDF Download */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
